import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

export const RecordingRetention = () => {
    const AUTH_TOKEN = localStorage.getItem('astroVani-admin-token');
    const [form, setForm] = useState({
        value: 1, // default
    });

    const [loading, setLoading] = useState(false);
    const [callRecordingEnabled, setCallRecordingEnabled] = useState(false);
    const [toggleLoading, setToggleLoading] = useState(false);

    // ✅ Handle change
    const handleChange = (e) => {
        const { value } = e.target;
        setForm({ value });
    };

    const fetchCurrentRetention = async () => {
        try {
            const token = localStorage.getItem('astroVani-admin-token');
            const res = await fetch(
                `${process.env.REACT_APP_BASEURL}/admin/recordingRetentionDays`,
                {
                    method: "GET",
                    headers: {
                        "Authorization": `${token}`
                    }
                }
            );
            if (!res.ok) throw new Error("Failed to fetch retention days");
            const data = await res.json();
            setForm({ value: data?.data?.value });
        } catch (err) {
            console.error(err);
            toast.error("Error fetching retention days");
        }
    };

    // ✅ Fetch Call Recording Status
    const fetchCallRecordingStatus = async () => {
        try {
            const token = localStorage.getItem('astroVani-admin-token');
            const res = await fetch(
                `${process.env.REACT_APP_BASEURL}/admin/callRecordingEnabled`,
                {
                    method: "GET",
                    headers: {
                        "Authorization": `${token}`
                    }
                }
            );
            if (res.ok) {
                const data = await res.json();
                const val = data?.data?.value ?? data?.value;
                if (typeof val === "boolean") {
                    setCallRecordingEnabled(val);
                }
            }
        } catch (err) {
            console.error("Error fetching call recording status:", err);
        }
    };

    // ✅ Toggle Call Recording
    const handleToggleCallRecording = async (e) => {
        const nextValue = e.target.checked;
        setToggleLoading(true);
        const token = localStorage.getItem('astroVani-admin-token');

        try {
            const res = await fetch(
                `${process.env.REACT_APP_BASEURL}/admin/callRecordingEnabled`,
                {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `${token}`
                    },
                    body: JSON.stringify({
                        value: nextValue
                    }),
                }
            );

            if (!res.ok) {
                const errData = await res.json().catch(() => null);
                throw new Error(errData?.message || "Failed to update call recording status");
            }

            const data = await res.json().catch(() => null);
            setCallRecordingEnabled(nextValue);
            toast.success(data?.message || `Call recording ${nextValue ? "enabled" : "disabled"} successfully!`);
        } catch (err) {
            console.error("Error updating call recording status:", err);
            toast.error(err.message || "Error updating call recording status");
        } finally {
            setToggleLoading(false);
        }
    };

    // ✅ Submit
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.value || form.value <= 0) {
            toast.error("Please enter valid retention days");
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('astroVani-admin-token');

            const res = await fetch(
                `${process.env.REACT_APP_BASEURL}/admin/recordingRetentionDays`,
                {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `${token}`
                    },
                    body: JSON.stringify({
                        key: "recordingRetentionDays",
                        value: Number(form.value),
                    }),
                }
            );

            if (!res.ok) throw new Error("Failed to update");

            const resData = await res.json().catch(() => null);
            toast.success(resData?.message || "Recording config updated successfully!");
        } catch (err) {
            console.error(err);
            toast.error("Error updating retention days");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCurrentRetention();
        fetchCallRecordingStatus();
    }, []);

    return (
        <div className="card-body modern-card p-3">
            {/* 🔹 Header */}
            <div className="d-flex justify-content-between align-items-center modern-header mb-3">
                <h5 className="fw-bold m-0">Recording Config</h5>
                <div className="form-check form-switch m-0 d-flex align-items-center">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="callRecordingToggle"
                        checked={callRecordingEnabled}
                        onChange={handleToggleCallRecording}
                        disabled={toggleLoading}
                        style={{ cursor: toggleLoading ? "not-allowed" : "pointer", width: "2.4em", height: "1.25em" }}
                    />
                    <label
                        className="form-check-label fw-semibold ms-2"
                        htmlFor="callRecordingToggle"
                        style={{ cursor: toggleLoading ? "not-allowed" : "pointer", userSelect: "none", fontSize: "14px" }}
                    >
                        {toggleLoading ? (
                            <span className="text-muted">Updating...</span>
                        ) : (
                            <>
                                Call Recording:{" "}
                                <span className={callRecordingEnabled ? "text-success" : "text-danger"}>
                                    {callRecordingEnabled ? "Enabled" : "Disabled"}
                                </span>
                            </>
                        )}
                    </label>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="modern-form">
                <div className="row g-3 small">

                    {/* 🔹 Static Info */}
                    <div className="col-md-12">
                        <label className="fw-semibold">Configuration Key</label>
                        <input
                            className="form-control form-control-sm"
                            value="recordingRetentionDays"
                            disabled
                        />
                    </div>

                    {/* 🔹 Editable Field */}
                    <div className="col-md-12">
                        <label>Retention Days</label>
                        <input
                            type="number"
                            className="form-control form-control-sm"
                            value={form.value}
                            min={1}
                            onChange={handleChange}
                            placeholder="Enter number of days"
                        />
                    </div>


                    {/* 🔹 Save Button */}
                    <div className="col-12 d-flex justify-content-end">
                        <button
                            type="submit"
                            className="btn btn-primary btn-sm px-4 modern-save"
                            disabled={loading}
                        >
                            {loading ? "Saving..." : "Save"}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default RecordingRetention;